import type { ToolTestResult } from '../../lib/api';

/**
 * Human-facing copy for each engine-level tool error. The runtime returns a terse
 * `ErrorCategory` code; partners testing a tool need to know what it means and what to try
 * next, so each code maps to a title and a hint. Surfaced by {@link ToolTestErrorCard}.
 *
 * Derived from the `ToolTestResult` error union so `TOOL_ERROR_COPY` stays exhaustive — a
 * new error code added to the API type is a compile error here until copy exists for it.
 */
export type ToolErrorCategory = Extract<ToolTestResult, { error: string }>['error'];

export interface ToolErrorCopy {
  title: string;
  hint: string;
}

export const TOOL_ERROR_COPY: Record<ToolErrorCategory, ToolErrorCopy> = {
  NOT_FOUND: {
    title: 'Tool not found',
    hint: 'The tool no longer exists for this partner. Reload the page.',
  },
  VALIDATION_ERROR: {
    title: 'Args failed validation',
    hint: 'The values you passed don’t match the tool’s input schema. The fields below are the ones that failed — your API was never called.',
  },
  CLAIM_MISMATCH: {
    title: 'Claim check failed',
    hint: 'This tool enforces claims: the args must match the partner_user’s stored values. Either no matching partner_user was found, or a value didn’t match. Your API was never called.',
  },
  UPGRADE_REQUIRED: {
    title: 'Higher scope required',
    hint: 'This tool requires a more privileged scope than the test ran with. Raise the Scope selector and run again.',
  },
  UPSTREAM_TIMEOUT: {
    title: 'Your API timed out',
    hint: 'The request was sent but no response arrived within the tool’s timeout. Check the endpoint, or raise the timeout on the tool.',
  },
  UPSTREAM_ERROR: {
    title: 'Couldn’t reach your API',
    hint: 'The resolved URL was rejected (non-HTTPS, private/loopback host, or a DNS/network failure). Tools can only call public HTTPS hosts.',
  },
  AUTH_FAILURE: {
    title: 'Credential couldn’t be resolved',
    hint: 'The credential this tool references is missing or inaccessible. Re-select a credential in the Authenticate step.',
  },
  CONFIGURATION_ERROR: {
    title: 'Lookup configuration is stale',
    hint: 'The mapped schema, contact relationship, filter fields, or returned fields changed. Review this lookup tool and save a valid replacement configuration.',
  },
};
